import type {View} from 'react-native';

import type {VideoPlayer} from 'expo-video';

import {
  action,
  type AnnotationMapEntry,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';

import type {CachedMediaFactory} from '../../../core/models/CachedMedia/CachedMedia.factory';
import type {CachedMediaModel} from '../../../core/models/CachedMedia/CachedMedia.model';
import type {Nullable} from '../../../utils/utility-types';

import {secondsToMinutesAndSeconds} from '../../../utils/datetime';
import {getUuid} from '../../../utils/getUuid';
import {logger} from '../../../utils/logger';
import {
  type Destructible,
  ReactionDisposeManager,
  type WithReactionDispose,
} from '../../../utils/store';
import {type WithCallbacks} from '../../../utils/store/WithCallbacks';

export abstract class BaseVideoPlayerState<
    P extends BaseVideoPlayerState.Player,
    C extends BaseVideoPlayerState.Container,
  >
  implements
    Destructible,
    WithCallbacks.Interface<BaseVideoPlayerState<P, C>>,
    WithReactionDispose
{
  abstract callbacks: WithCallbacks.Manager<BaseVideoPlayerState<P, C>>;
  container: Nullable<C> = null;
  abstract currentTime: number;
  disposer = new ReactionDisposeManager();
  player: Nullable<P> = null;
  volume = 0.5;
  protected allowFullscreen: boolean;
  protected disableCache = false;
  protected fullscreenMethods: BaseVideoPlayerState.FullscreenMethods = {
    enterFullscreen: () => Promise.resolve(),
    exitFullscreen: () => Promise.resolve(),
  };
  protected media: CachedMediaModel;
  protected url: Nullable<string> = null;

  protected constructor(
    props: BaseVideoPlayerState.Props,
    cachedMediaFactory: CachedMediaFactory,
  ) {
    this.media = cachedMediaFactory.create();
    this.allowFullscreen = Boolean(props.allowFullscreen);

    makeObservable<
      BaseVideoPlayerState<P, C>,
      'disableCache' | 'fullscreenMethods' | 'isMediaUrlReady' | 'url'
    >(this, {
      container: observable.ref,
      disableCache: observable,
      fullscreenMethods: false,
      init: action.bound,
      isMediaUrlReady: computed,
      player: observable.ref,
      setContainer: action.bound,
      setFullscreenMethods: action.bound,
      setPlayer: action.bound,
      src: computed,
      url: observable,
      volume: observable,
    });
  }

  async init({disableCache, src}: BaseVideoPlayerState.InitialProps) {
    this.url = src;
    this.disableCache = Boolean(disableCache);
    await this.media.init({
      disableCache,
      id: getUuid(),
      src: this.url,
    });
  }

  destruct(): void {
    runInAction(() => {
      this.player = null;
    });
    this.disposer.dispose();
  }

  get currentTimeSec() {
    return Math.floor(this.currentTime);
  }

  abstract get isEnded(): boolean;

  abstract get isLoaded(): boolean;

  abstract get isMobile(): boolean;

  abstract get isMuted(): boolean;
  abstract get isPaused(): boolean;

  get PROGRESS_BAR_STEP() {
    return 0.01;
  }

  get renderTime() {
    const current = secondsToMinutesAndSeconds(this.currentTimeSec);
    const total = secondsToMinutesAndSeconds(this.totalTimeSec);

    return `${current} / ${total}`;
  }

  abstract get shouldShowControls(): boolean;

  get src() {
    return this.media.src;
  }

  abstract get totalTime(): number;

  get totalTimeSec() {
    return Math.floor(this.totalTime);
  }

  protected get isMediaUrlReady() {
    if (this.disableCache) {
      return true;
    }

    return this.media.isLoaded;
  }

  static makeObservable<
    T extends BaseVideoPlayerState<
      BaseVideoPlayerState.Player,
      BaseVideoPlayerState.Container
    >,
  >(thisArg: T, overrides: Partial<Record<keyof T, AnnotationMapEntry>>) {
    makeObservable(
      thisArg,
      Object.assign(
        {
          isEnded: computed,
          isLoaded: computed,
          isMobile: computed,
          isMuted: computed,
          isPaused: computed,
          pause: action.bound,
          play: action.bound,
          seek: action.bound,
          shouldShowControls: computed,
          toggleFullscreen: action.bound,
          toggleMute: action.bound,
          togglePlay: action.bound,
          totalTimeSec: computed,
        },
        overrides,
      ),
    );
  }

  onError(error: any) {
    logger.error(error);
  }

  abstract pause(): void;

  abstract play(): void;

  abstract seek(time: number): void;

  setContainer(container: C): void {
    this.container = container;
  }

  setFullscreenMethods(methods: BaseVideoPlayerState.FullscreenMethods) {
    this.fullscreenMethods = methods;
  }

  setPlayer(player: P): void {
    this.player = player;
  }

  abstract toggleFullscreen(): void;

  abstract toggleMute(): void;

  abstract togglePlay(): void;

  volumeChangeHandler(_event: any) {}

  protected initCallbacks(props: BaseVideoPlayerState.Props) {
    this.callbacks.setCallbacks(props.callbacks);
    this.callbacks.init(this);
  }

  protected prepareContainer() {}

  protected abstract preparePlayer(): void;

  protected setupReactions() {
    this.disposer.watch(
      'preparePlayer',
      reaction(
        () => this.player,
        player => {
          if (player) {
            this.preparePlayer();
          }
        },
        {
          fireImmediately: true,
        },
      ),
    );

    this.disposer.watch(
      'prepareContainer',

      reaction(
        () => this.container,
        container => {
          if (container) {
            this.prepareContainer();
          }
        },
        {
          fireImmediately: true,
        },
      ),
    );
  }
}

export namespace BaseVideoPlayerState {
  export type Container = HTMLDivElement | View;
  export type DOMContainer = HTMLDivElement;

  export type DOMPlayer = HTMLVideoElement;
  export type FullscreenMethods = {
    enterFullscreen: () => Promise<void>;
    exitFullscreen: () => Promise<void>;
  };
  export type InitialProps = {
    disableCache?: boolean;
    src: Nullable<string>;
  };
  export type NativeContainer = View;
  export type NativePlayer = VideoPlayer;
  export type Player = HTMLVideoElement | NativePlayer;
  export type Props = {
    allowFullscreen?: boolean;
    callbacks?: WithCallbacks.Callbacks<
      BaseVideoPlayerState<Player, Container>
    >;
    showControls?: boolean;
  };
}
